import type { GaFormOptions, GaFormStatus } from '../analytics/types'

export const buildSearchGaFormProps = (
  options: GaFormOptions,
): Omit<
  import('@/features/analytics/gaForm/GaForm').GaFormProps,
  'children'
> => {
  const {
    baseGaFormProps,
    onSubmit,
    formStatus,
    formFieldCausingError,
    payload,
    totalCount,
  } = options

  return {
    ...baseGaFormProps,
    getFormContent: () =>
      buildFormContent((payload as { searchText?: string })?.searchText),
    getFormAdditionalContent: () => buildFormAdditionalContent(payload),
    getFormContent2: () => `Search Results: ${totalCount}`,
    formStatus: formStatus ?? null,
    formFieldCausingError: formFieldCausingError ?? null,
    deferGaSubmit: true,
    onSubmit,
  }
}

export const buildFormContent = (searchText: string | null | undefined) => {
  const trimmed = searchText?.trim()
  return trimmed ? `Search by Media Number or Text: ${trimmed}` : null
}

export const buildFormAdditionalContent = (payload: unknown) => {
  const entries: string[] = []
  const shouldSkipKey = (key: string) => key === 'searchText'

  const visit = (value: unknown, path: string) => {
    const lastKey = path.split('.').pop() ?? ''
    if (shouldSkipKey(lastKey)) return

    const leafValue = toValueString(value)
    if (leafValue !== null) {
      entries.push(`${toLabel(path)}: ${leafValue}`)
      return
    }

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const record = value as Record<string, unknown>
      for (const [key, nextValue] of Object.entries(record)) {
        if (shouldSkipKey(key)) continue
        const nextPath = path ? `${path}.${key}` : key
        visit(nextValue, nextPath)
      }
    }
  }

  if (payload && typeof payload === 'object') {
    for (const [key, value] of Object.entries(
      payload as Record<string, unknown>,
    )) {
      if (shouldSkipKey(key)) continue
      visit(value, key)
    }
  }

  return entries.length ? entries.join(' | ') : null
}

const toLabel = (path: string) => {
  const last = path.split('.').pop() ?? path
  return last
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (char) => char.toUpperCase())
}

const toValueString = (value: unknown): string | null => {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    return trimmed.length ? trimmed : null
  }
  if (typeof value === 'number')
    return Number.isFinite(value) ? String(value) : null
  if (typeof value === 'boolean') return value ? 'true' : null
  if (Array.isArray(value)) {
    const items = value
      .map((item) => toValueString(item))
      .filter((item): item is string => Boolean(item))
    return items.length ? items.join(', ') : null
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>
    const labelKeys = ['label', 'description', 'name', 'valueCode', 'code']
    for (const key of labelKeys) {
      const labelLike = record[key]
      if (typeof labelLike === 'string') {
        const trimmed = labelLike.trim()
        return trimmed.length ? trimmed : null
      }
    }
  }
  return null
}

export const getFormStatus = (
  isError: boolean,
  totalCount: number,
): GaFormStatus => {
  if (isError) return 'error'
  if (!isError && totalCount === 0) return 'fail'
  return 'success'
}

export const getFormFieldCausingError = (
  isSuccess: boolean,
  totalCount: number,
): string | null => {
  if (isSuccess && totalCount === 0) return 'no results found'
  return null
}
